/**
 * Client Overview Tab
 * 
 * Shows profile summary, contacts, funding sources, and medical info.
 */
import React from "react";
import { 
  User, Phone, Mail, MapPin, Globe, Heart, Shield, DollarSign,
  AlertTriangle, Users, FileText, Edit
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Separator } from "../../../components/ui/separator";

export default function ClientOverviewTab({ client, onUpdate }) {
  if (!client) return null;

  const contacts = client.contacts || [];
  const emergencyContacts = contacts.filter(c => c.contact_type === "emergency");
  const authorizedReps = contacts.filter(c => c.contact_type === "authorized_representative");
  const physicians = contacts.filter(c => c.contact_type === "physician");
  
  const fundingSources = client.funding_sources || [];
  const primaryFunding = fundingSources.find(f => f.is_primary);
  
  const medicalInfo = client.medical_info || {};
  const serviceReq = client.service_requirements || {};

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="overview-tab">
      {/* Demographics Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="w-5 h-5" />
              Demographics
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Full Name</span>
              <p className="font-medium">{client.first_name} {client.last_name}</p>
            </div>
            {client.preferred_name && (
              <div>
                <span className="text-muted-foreground">Preferred Name</span>
                <p className="font-medium">{client.preferred_name}</p>
              </div>
            )}
            {client.date_of_birth && (
              <div>
                <span className="text-muted-foreground">Date of Birth</span>
                <p className="font-medium">{new Date(client.date_of_birth).toLocaleDateString()}</p>
              </div>
            )}
            {client.gender && (
              <div>
                <span className="text-muted-foreground">Gender</span>
                <p className="font-medium">{client.gender}</p>
              </div>
            )}
            <div>
              <span className="text-muted-foreground">Primary Language</span>
              <p className="font-medium">{client.primary_language || "English"}</p>
              {client.interpreter_needed && (
                <Badge variant="outline" className="mt-1 text-xs">Interpreter Needed</Badge>
              )}
            </div>
          </div>
          
          <Separator />
          
          <div className="space-y-2">
            <span className="text-sm text-muted-foreground">Address</span>
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 text-muted-foreground" />
              <div>
                <p className="font-medium">{client.address_line1}</p>
                {client.address_line2 && <p>{client.address_line2}</p>}
                <p>{client.city}, {client.state} {client.zip_code}</p>
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4">
            {client.primary_phone && (
              <a href={`tel:${client.primary_phone}`} className="flex items-center gap-2 text-sm hover:text-primary">
                <Phone className="w-4 h-4" />
                {client.primary_phone}
              </a>
            )}
            {client.email && (
              <a href={`mailto:${client.email}`} className="flex items-center gap-2 text-sm hover:text-primary">
                <Mail className="w-4 h-4" />
                {client.email}
              </a>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Emergency Contacts */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Emergency Contacts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {emergencyContacts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No emergency contacts on file</p>
          ) : (
            <div className="space-y-4">
              {emergencyContacts.map((contact) => (
                <div key={contact.id} className="flex items-start justify-between">
                  <div>
                    <p className="font-medium">{contact.name}</p>
                    <p className="text-sm text-muted-foreground">{contact.relationship}</p>
                    {contact.phone && (
                      <a href={`tel:${contact.phone}`} className="text-sm hover:text-primary flex items-center gap-1 mt-1">
                        <Phone className="w-3 h-3" />
                        {contact.phone}
                      </a>
                    )}
                  </div>
                  {contact.is_primary && (
                    <Badge variant="outline" className="text-xs">Primary</Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Funding Source */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <DollarSign className="w-5 h-5 text-green-600" />
            Funding Source
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!primaryFunding ? (
            <p className="text-sm text-muted-foreground">No funding source on file</p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Badge className="capitalize">
                  {primaryFunding.source_type.replace(/_/g, " ")}
                </Badge>
                {primaryFunding.is_active && (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Active</Badge>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-3 text-sm">
                {primaryFunding.member_id && (
                  <div>
                    <span className="text-muted-foreground">Member ID</span>
                    <p className="font-medium">{primaryFunding.member_id}</p>
                  </div>
                )}
                {primaryFunding.authorization_number && (
                  <div>
                    <span className="text-muted-foreground">Authorization #</span>
                    <p className="font-medium">{primaryFunding.authorization_number}</p>
                  </div>
                )}
                {primaryFunding.authorized_hours_weekly && (
                  <div>
                    <span className="text-muted-foreground">Auth Hours/Week</span>
                    <p className="font-medium">{primaryFunding.authorized_hours_weekly}</p>
                  </div>
                )}
                {primaryFunding.authorized_end_date && (
                  <div>
                    <span className="text-muted-foreground">Auth End Date</span>
                    <p className="font-medium">{new Date(primaryFunding.authorized_end_date).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Medical Summary */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Heart className="w-5 h-5 text-red-500" />
            Medical Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {medicalInfo.primary_diagnosis && (
            <div>
              <span className="text-sm text-muted-foreground">Primary Diagnosis</span>
              <p className="font-medium">{medicalInfo.primary_diagnosis}</p>
            </div>
          )}
          {medicalInfo.allergies_summary && (
            <div>
              <span className="text-sm text-muted-foreground">Allergies</span>
              <p className="font-medium">{medicalInfo.allergies_summary}</p>
            </div>
          )}
          {medicalInfo.medications_summary && (
            <div>
              <span className="text-sm text-muted-foreground">Medications</span>
              <p className="font-medium">{medicalInfo.medications_summary}</p>
            </div>
          )}
          
          <div className="flex gap-2 pt-2">
            {medicalInfo.has_dnr && (
              <Badge variant="destructive">DNR on File</Badge>
            )}
            {medicalInfo.has_advanced_directive && (
              <Badge variant="outline">Advance Directive</Badge>
            )}
          </div>
          
          {medicalInfo.primary_physician_name && (
            <div className="pt-2 border-t">
              <span className="text-sm text-muted-foreground">Primary Physician</span>
              <p className="font-medium">{medicalInfo.primary_physician_name}</p>
              {medicalInfo.primary_physician_phone && (
                <a href={`tel:${medicalInfo.primary_physician_phone}`} className="text-sm hover:text-primary flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {medicalInfo.primary_physician_phone}
                </a>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Authorized Representatives */}
      {authorizedReps.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Shield className="w-5 h-5 text-blue-600" />
              Authorized Representatives
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {authorizedReps.map((rep) => (
                <div key={rep.id} className="flex items-start justify-between">
                  <div>
                    <p className="font-medium">{rep.name}</p>
                    <p className="text-sm text-muted-foreground">{rep.relationship}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {rep.has_poa && (
                        <Badge variant="outline" className="text-xs">POA</Badge>
                      )}
                      {rep.can_make_care_decisions && (
                        <Badge variant="outline" className="text-xs">Care Decisions</Badge>
                      )}
                      {rep.can_sign_documents && (
                        <Badge variant="outline" className="text-xs">Can Sign</Badge>
                      )}
                    </div>
                    {rep.phone && (
                      <a href={`tel:${rep.phone}`} className="text-sm hover:text-primary flex items-center gap-1 mt-1">
                        <Phone className="w-3 h-3" />
                        {rep.phone}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Service Requirements */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="w-5 h-5 text-purple-600" />
            Service Requirements
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            {serviceReq.recommended_hours_per_week && (
              <div>
                <span className="text-muted-foreground">Hours/Week</span>
                <p className="font-medium">{serviceReq.recommended_hours_per_week}</p>
              </div>
            )}
            {serviceReq.preferred_times && (
              <div>
                <span className="text-muted-foreground">Preferred Times</span>
                <p className="font-medium">{serviceReq.preferred_times}</p>
              </div>
            )}
            {serviceReq.caregiver_gender_preference && (
              <div>
                <span className="text-muted-foreground">Caregiver Gender</span>
                <p className="font-medium">{serviceReq.caregiver_gender_preference}</p>
              </div>
            )}
            {serviceReq.caregiver_language_requirement && (
              <div>
                <span className="text-muted-foreground">Language Required</span>
                <p className="font-medium">{serviceReq.caregiver_language_requirement}</p>
              </div>
            )}
          </div>
          
          {serviceReq.preferred_days && serviceReq.preferred_days.length > 0 && (
            <div>
              <span className="text-sm text-muted-foreground">Preferred Days</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {serviceReq.preferred_days.map(day => (
                  <Badge key={day} variant="outline" className="capitalize">{day}</Badge>
                ))}
              </div>
            </div>
          )}
          
          {serviceReq.special_instructions && (
            <div className="pt-2 border-t">
              <span className="text-sm text-muted-foreground">Special Instructions</span>
              <p className="text-sm mt-1">{serviceReq.special_instructions}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
